import { useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { 
    Box, Typography, IconButton, Card, CardContent, Avatar, Grid, Paper, 
    Chip, Dialog, DialogTitle, DialogContent, DialogContentText, 
    DialogActions, Button, TextField, CircularProgress, Snackbar, Alert ,Stack
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import BarChartIcon from "@mui/icons-material/BarChart";
import DeleteIcon from "@mui/icons-material/Delete";

import { updateDeliveryBoy, deleteDeliveryBoy } from "../../api/adminApi";
import RiderFinance from "./RiderFinance";

const modernInputStyle = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        bgcolor: '#FFFFFF',
        '& fieldset': { borderColor: '#ECE5DA' },
        '&:hover fieldset': { borderColor: '#F29D17' },
        '&.Mui-focused fieldset': { borderColor: '#F8C332', borderWidth: '2px' }
    }
};

export default function RiderProfile() {
    const navigate = useNavigate();
    const location = useLocation();
    
    const financeRef = useRef(null);
    const [rider, setRider] = useState(location.state?.rider);

    const [openEdit, setOpenEdit] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [showFinance, setShowFinance] = useState(false); 
    const [actionLoading, setActionLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    const [form, setForm] = useState({ name: "", phone: "", password: "" });

    if (!rider) return <Typography p={3}>Rider not found.</Typography>;

    const showMessage = (msg, type = "success") => setSnackbar({ open: true, message: msg, severity: type });

    const handleOpenEdit = () => {
        setForm({ name: rider.name, phone: rider.phone || "", password: "" });
        setOpenEdit(true);
    };

    const handleEdit = async () => {
        try {
            setActionLoading(true);
            await updateDeliveryBoy(rider.id, form);
            setRider({ ...rider, name: form.name, phone: form.phone });
            setOpenEdit(false);
            showMessage("Rider updated successfully!");
        } catch (err) {
            showMessage(err?.response?.data?.detail || "Failed to update rider", "error");
        } finally {
            setActionLoading(false);
        }
    };

    const handleDelete = async () => {
        try {
            setActionLoading(true);
            await deleteDeliveryBoy(rider.id);
            setOpenDelete(false);
            showMessage("Rider deleted successfully.");
            setTimeout(() => navigate(-1), 1000); 
        } catch (err) {
            showMessage(err?.response?.data?.detail || "Failed to delete rider", "error");
        } finally {
            setActionLoading(false);
        }
    };

    const handleToggleFinance = () => {
        setShowFinance((prev) => {
            const nextState = !prev;
            if (nextState) {
                setTimeout(() => {
                    financeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 150);
            }
            return nextState;
        });
    };

    return (
        // 👇 FIXED: Added pb: { xs: 15, sm: 8 } back to ensure enough space to scroll past the fixed bottom nav!
        <Box sx={{ width: "100%", minHeight: "100vh", bgcolor: "#FFFDF8", p: { xs: 2, sm: 3 }, pb: { xs: 15, sm: 8 }, boxSizing: 'border-box' }}>
            
            <Box display="flex" alignItems="center" gap={1.5} mb={3}>
                <IconButton onClick={() => navigate(-1)} sx={{ bgcolor: '#FFFFFF', border: '1px solid #ECE5DA' }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h5" fontWeight="900" color="#3A2C23">Rider Profile</Typography>
            </Box>

            <Card elevation={0} sx={{ borderRadius: '24px', border: "1px solid #ECE5DA", mb: 4, bgcolor: "#FFFFFF" }}>
                <CardContent sx={{ display: "flex", gap: 2, p: 3, alignItems: "flex-start" }}>
                    <Avatar sx={{ bgcolor: "#FFF6DE", color: "#F29D17", width: 60, height: 60, fontSize: "1.5rem", fontWeight: 900 }}>
                        {rider.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box flex={1}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <Typography variant="h6" fontWeight="900" color="#3A2C23">{rider.name}</Typography>
                            <Chip label={rider.status || "IDLE"} size="small" sx={{ bgcolor: "#DDECC8", color: "#577A34", fontWeight: 800, height: 20 }} />
                        </Box>
                        <Typography variant="body2" color="#6E655E" fontWeight="600" mb={0.5}>🏢 ID: {rider.id}</Typography>
                        <Typography variant="body2" color="#6E655E" fontWeight="600" mb={0.5}>✉ {rider.email}</Typography>
                        <Typography variant="body2" color="#6E655E" fontWeight="600" mb={0.5}>📞 {rider.phone}</Typography>
                        <Typography variant="body2" color="#E85D3F" fontWeight="800" mb={0.5}>📍 Zone: {rider.zone}</Typography>
                        <Typography variant="body2" color="#577A34" fontWeight="800">🎯 Zonal Admin ID: {rider.zonal_admin_id}</Typography>
                    </Box>
                </CardContent>
            </Card>

            <Typography variant="h6" fontWeight="900" color="#3A2C23" mb={2}>Rider Management</Typography>

            <Grid container spacing={2} mb={2}>
                <Grid item xs={4}>
                    <Paper 
                        elevation={0} 
                        onClick={handleOpenEdit}
                        sx={{ p: 2, borderRadius: '20px', border: '1px solid #ECE5DA', textAlign: 'center', cursor: 'pointer', '&:hover': { bgcolor: '#F9F9F9' } }}
                    >
                        <Box sx={{ width: 50, height: 50, mx: "auto", mb: 1, borderRadius: '14px', bgcolor: "#E1EED3", display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <EditIcon sx={{ color: "#577A34" }} />
                        </Box>
                        <Typography variant="caption" fontWeight="800" color="#3A2C23">Edit Rider</Typography>
                    </Paper>
                </Grid>

                <Grid item xs={4}>
                    <Paper 
                        elevation={0} 
                        onClick={handleToggleFinance} 
                        sx={{ p: 2, borderRadius: '20px', border: showFinance ? '2px solid #F8C332' : '1px solid #ECE5DA', textAlign: 'center', cursor: 'pointer', '&:hover': { bgcolor: '#F9F9F9' } }}
                    >
                        <Box sx={{ width: 50, height: 50, mx: "auto", mb: 1, borderRadius: '14px', bgcolor: "#FFF6DE", display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <BarChartIcon sx={{ color: "#D97706" }} />
                        </Box>
                        <Typography variant="caption" fontWeight="800" color="#3A2C23">Finance</Typography>
                    </Paper>
                </Grid>

                <Grid item xs={4}>
                    <Paper 
                        elevation={0} 
                        onClick={() => setOpenDelete(true)} 
                        sx={{ p: 2, borderRadius: '20px', border: '1px solid #ECE5DA', textAlign: 'center', cursor: 'pointer', '&:hover': { bgcolor: '#F9F9F9' } }}
                    >
                        <Box sx={{ width: 50, height: 50, mx: "auto", mb: 1, borderRadius: '14px', bgcolor: "#FFF0EE", display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <DeleteIcon sx={{ color: "#E85D3F" }} />
                        </Box>
                        <Typography variant="caption" fontWeight="800" color="#3A2C23">Delete</Typography>
                    </Paper>
                </Grid>
            </Grid>

            {showFinance && (
                <Box mt={3} ref={financeRef} sx={{ scrollMarginTop: "20px" }}>
                    <RiderFinance riderId={rider.id} />
                </Box>
            )}

            <Dialog open={openEdit} onClose={() => setOpenEdit(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: "24px", p: 1 } }}>
                <DialogTitle sx={{ fontWeight: "900", color: "#3A2C23" }}>Edit Rider</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField label="Name" fullWidth value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} sx={modernInputStyle} />
                        <TextField label="Phone" fullWidth value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} sx={modernInputStyle} />
                        <TextField label="New Password (leave blank to keep current)" type="password" fullWidth value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} sx={modernInputStyle} />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setOpenEdit(false)} sx={{ color: "#6E655E", fontWeight: "800" }}>Cancel</Button>
                    <Button variant="contained" disableElevation onClick={handleEdit} disabled={actionLoading} sx={{ borderRadius: "12px", bgcolor: "#F8C332", color: "#3A2C23", fontWeight: "900" }}>
                        {actionLoading ? <CircularProgress size={24} /> : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openDelete} onClose={() => setOpenDelete(false)} PaperProps={{ sx: { borderRadius: "24px", p: 1 } }}>
                <DialogTitle sx={{ fontWeight: "900", color: "#3A2C23" }}>Delete Rider?</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ fontWeight: "600", color: "#6E655E" }}>
                        Are you sure you want to remove <b>{rider.name}</b>? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setOpenDelete(false)} sx={{ color: "#6E655E", fontWeight: "800" }}>Cancel</Button>
                    <Button variant="contained" disableElevation onClick={handleDelete} disabled={actionLoading} sx={{ borderRadius: "12px", bgcolor: "#E85D3F", color: "white", fontWeight: "900", "&:hover": { bgcolor: "#D64A2C" } }}>
                        {actionLoading ? <CircularProgress size={24} sx={{ color: "white" }} /> : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%', borderRadius: '12px', fontWeight: 'bold' }}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
}